import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ApiPublicGuideService } from '../../../core/services/api/PublicGuide/api-public-guide';
import { PublicGuideDetail } from '../../../core/models/public-guide.model';
import { ImageViewerComponent } from '../../../shared/components/image-viewer/image-viewer.component';
import { ManualHeroComponent } from '../manual-hero/manual-hero.component';

@Component({
  selector: 'app-manual-guide-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, ImageViewerComponent, ManualHeroComponent],
  templateUrl: './manual-guide-detail.component.html',
  styleUrl: './manual-guide-detail.component.css',
})
export class ManualGuideDetailComponent implements OnInit {
  private readonly api = inject(ApiPublicGuideService);
  private readonly route = inject(ActivatedRoute);
  private readonly sanitizer = inject(DomSanitizer);

  kind = signal<'parking' | 'ev'>('parking');
  detail = signal<PublicGuideDetail | null>(null);
  isLoading = signal(false);
  notFound = signal(false);
  viewerImages = signal<string[]>([]);
  viewerIndex = signal(0);
  isViewerOpen = signal(false);

  ngOnInit(): void {
    const rawKind = (this.route.snapshot.paramMap.get('kind') || 'parking').toLowerCase();
    this.kind.set(rawKind === 'ev' ? 'ev' : 'parking');
    const slug = this.route.snapshot.paramMap.get('slug')?.trim() || '';
    if (!slug) {
      this.notFound.set(true);
      return;
    }
    this.load(slug);
  }

  load(slug: string): void {
    this.isLoading.set(true);
    this.notFound.set(false);
    this.api.getPublicGuideBySlug(slug).subscribe({
      next: (d) => {
        this.detail.set(d);
        this.isLoading.set(false);
      },
      error: () => {
        this.notFound.set(true);
        this.isLoading.set(false);
      },
    });
  }

  youtubeEmbedUrl(videoId: string | null | undefined): SafeResourceUrl | null {
    if (!videoId || !/^[A-Za-z0-9_-]{11}$/.test(videoId)) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube-nocookie.com/embed/${videoId}`,
    );
  }

  /** ปก + รูปทุกบล็อก ตามลำดับที่แสดงบนหน้า */
  galleryImages(): string[] {
    const d = this.detail();
    if (!d) return [];
    const list: string[] = [];
    if (d.coverImage) list.push(d.coverImage);
    for (const block of d.blocks || []) {
      if (block.blockType === 'image' && block.image) list.push(block.image);
    }
    return list;
  }

  openZoom(url: string | null | undefined): void {
    if (!url) return;
    const images = this.galleryImages();
    const index = images.indexOf(url);
    this.viewerImages.set(index >= 0 ? images : [url]);
    this.viewerIndex.set(Math.max(0, index));
    this.isViewerOpen.set(true);
  }

  closeZoom(): void {
    this.isViewerOpen.set(false);
  }

  kindLabel(): string {
    return this.kind() === 'ev' ? 'คู่มือ EV Charger' : 'คู่มือ Parking';
  }
}
